import { collection, getDocs } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { governanceUserSchema, type GovernanceUser } from "./schemas";

export type GovernanceUsersState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: GovernanceUser[] };

export function useGovernanceUsers(): GovernanceUsersState & {
  reload: () => void;
} {
  const [state, setState] = useState<GovernanceUsersState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Deliberately unordered: Firestore excludes documents missing the
    // ordered field, which would hide users from the only surface that can
    // change their platform role. Sort client-side instead.
    getDocs(collection(db, "users"))
      .then((snap) => {
        if (cancelled) return;
        const users: GovernanceUser[] = [];
        snap.forEach((d) => {
          const result = governanceUserSchema.safeParse({
            uid: d.id,
            ...d.data(),
          });
          if (result.success) users.push(result.data);
        });
        users.sort((a, b) => (a.email ?? "").localeCompare(b.email ?? ""));
        setState({ status: "success", data: users });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load users",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
